import React from 'react';

interface Props {
  /** Primary color (= OPT primary_color). May be null for gradient/multicolor
   *  rows that have no dominant color. */
  primary?: string | null;
  /** Secondary colors (= OPT secondary_colors_0..4). At most 5 entries. */
  secondaries?: string[] | null;
  /** OpenPrintTag tag list — drives the visual finish (matte, silk, glow,
   *  translucent, transparent, glitter, etc). */
  tags?: string[] | null;
  size?: 'sm' | 'md' | 'lg';
  /** Exact diameter in px. Wins over `size` — for callers on their own scale
   *  (piece dots at 16, print rows at 32) that still need the multicolour and
   *  finish rendering rather than a flat circle of their own. */
  sizePx?: number;
  /** Native tooltip. Omit it when the swatch sits inside a `Popover`, or when
   *  the name is already written beside it — there is no hex fallback. */
  title?: string;
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<Props['size']>, string> = {
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
  lg: 'w-10 h-10',
};

/**
 * Renders a circular color swatch from OpenPrintTag-shaped color data.
 *
 * - If both primary and secondaries are absent, renders a black placeholder.
 * - With only a primary: a flat (or finish-styled) swatch.
 * - With secondaries (with or without primary): conic-gradient segmented in
 *   the order [primary, ...secondaries] — primary first if present.
 *
 * The visual finish picks up tags rather than a separate `color_finish` field:
 * `silk` → linear highlight, `glow_in_the_dark` / `pearlescent` / `glitter`
 * → radial highlight, `transparent` / `translucent` → opacity ring.
 *
 * MULTICOLOUR WINS OVER FINISH, on purpose. When a material has several colours
 * the wheel is what identifies it — a `PLA Chameleon Galaxy Blue` tagged
 * `iridescent` + `glitter` renders as the wheel with no sheen. Which colours a
 * spool is answers "is this the right one?"; the finish only decorates it, and
 * layering a highlight over the wedges muddies both.
 *
 * Used by both tenant catalogo and platform-admin catalogo. Lives in
 * `shared/ui/` because the rendering logic mirrors the OpenPrintTag spec
 * exactly and never diverges between the two apps.
 */
export default function ColorSwatch({
  primary,
  secondaries,
  tags,
  size = 'md',
  sizePx,
  title,
  className = '',
}: Props) {
  const baseClass = sizePx ? '' : SIZE_CLASS[size];
  const tagSet = new Set(tags ?? []);

  const colors: string[] = [];
  if (primary) colors.push(primary);
  for (const c of secondaries ?? []) {
    if (c && !colors.includes(c)) colors.push(c);
  }
  const fallback = '#888888';
  const main = colors[0] ?? fallback;
  const isMulti = colors.length > 1;

  let style: React.CSSProperties = { background: main };

  if (isMulti) {
    const step = 100 / colors.length;
    const stops = colors
      .map((c, i) => `${c} ${i * step}% ${(i + 1) * step}%`)
      .join(', ');
    style = { background: `conic-gradient(${stops})` };
  } else if (tagSet.has('silk') || tagSet.has('contains_metal')) {
    style = {
      background: `linear-gradient(135deg, ${main}, color-mix(in srgb, ${main}, white 35%) 50%, ${main})`,
    };
  } else if (
    tagSet.has('glow_in_the_dark')
    || tagSet.has('pearlescent')
    || tagSet.has('glitter')
    || tagSet.has('iridescent')
  ) {
    style = {
      background: `radial-gradient(circle at 30% 30%, color-mix(in srgb, ${main}, white 45%), ${main} 70%)`,
    };
  } else if (tagSet.has('matte')) {
    // Slight darken for matte
    style = { background: main, filter: 'saturate(0.85)' };
  }

  let extraClass = '';
  if (tagSet.has('transparent')) {
    extraClass = 'opacity-40 ring-2 ring-inset ring-slate-300 dark:ring-slate-600';
  } else if (tagSet.has('translucent')) {
    extraClass = 'opacity-70 ring-1 ring-inset ring-slate-200 dark:ring-slate-700';
  }

  // NO hex fallback for the tooltip. It used to be `title ?? main`, so a caller
  // that deliberately omitted `title` still got a native "#9CA3AF" bubble — which
  // told the user nothing AND, next to a caller with its own popover, meant two
  // tooltips on screen at once. Without a title the swatch is decoration whose
  // meaning is in the adjacent text, so it is hidden from screen readers too.
  return (
    <span
      className={`inline-block shrink-0 rounded-full border border-border-subtle dark:border-border-subtle-dark ${baseClass} ${extraClass} ${className}`}
      style={sizePx ? { ...style, width: sizePx, height: sizePx } : style}
      title={title}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    />
  );
}
